import {
  Badge,
  Box,
  Card,
  IconArrow,
  IconMinus,
  IconTick,
  Inline,
  Stack,
  Text,
} from 'braid-design-system';

import { Link, TextLink } from 'src/components/Link/Link';
import type { Partner } from 'src/types';
import { makePartnerImageUrl } from 'src/utils/partners/makePartnerImageUrl';
import { slugify } from 'src/utils/slugify';

import * as styles from './PartnerCard.css';

interface CardProps {
  partner: Partner;
  tier: string;
}
export const PartnerCard = ({ partner, tier }: CardProps) => {
  const {
    name,
    slug,
    siteUrl: website,
    description: blurb,
    imageUrl: icon,
    hasMiddlewareRequirement: middleware,
    hasApacIntegration: connectedApacIntegration,
  } = partner;

  const availableTypeCodes = partner.configurableRelationships.map(
    ({ typeCode }) => typeCode,
  );
  const hasJobPosting = availableTypeCodes.includes('JOB_POSTING');
  const hasOptimisedApply = availableTypeCodes.includes('APPLICATION_EXPORT');
  const hasApplyWithSeek = availableTypeCodes.includes('APPLICATION_PREFILL');

  const websiteWithProtocol = website?.startsWith('https://')
    ? website
    : `https://${website}`;

  const nameLabel = (
    <Text size="small" weight="strong">
      {website ? (
        <TextLink
          href={websiteWithProtocol}
          skipResolvedUrl={true}
          target="_blank"
          tracking={`${tier}-${slug || slugify({ str: name })}-link-clicked`}
          icon={<IconArrow direction="right" />}
          iconPosition="trailing"
          weight="weak"
        >
          {name}
        </TextLink>
      ) : (
        name
      )}
    </Text>
  );

  return (
    <Card height="full">
      <Stack space="gutter">
        {icon ? (
          <Stack space="xsmall">
            <Link
              href={websiteWithProtocol}
              skipResolvedUrl={true}
              target="_blank"
              tracking={`${tier}-${
                slug || slugify({ str: name })
              }-link-clicked`}
            >
              <Box
                component="img"
                className={styles.partnerIcon}
                src={makePartnerImageUrl(icon)}
              />
            </Link>
            {nameLabel}
          </Stack>
        ) : (
          <Box>{nameLabel}</Box>
        )}
        {blurb ? <Text size="small">{blurb}</Text> : null}
        <Stack space="medium">
          <Stack space="xsmall">
            <Box style={{ opacity: hasJobPosting ? '1' : '0.5' }}>
              <Inline space="xsmall" alignY="center">
                {hasJobPosting ? (
                  <IconTick tone="positive" size="xsmall" />
                ) : (
                  <IconMinus tone="secondary" size="xsmall" />
                )}
                <Text
                  size="small"
                  tone={hasJobPosting ? 'neutral' : 'secondary'}
                >
                  <span>SEEK Job Posting </span>
                  {middleware && <span>*</span>}
                </Text>
              </Inline>
            </Box>
            <Box style={{ opacity: hasOptimisedApply ? '1' : '0.5' }}>
              <Inline space="xsmall" alignY="center">
                {hasOptimisedApply ? (
                  <IconTick tone="positive" size="xsmall" />
                ) : (
                  <IconMinus tone="secondary" size="xsmall" />
                )}
                <Text
                  size="small"
                  tone={hasOptimisedApply ? 'neutral' : 'secondary'}
                >
                  SEEK Optimised Apply
                </Text>
              </Inline>
            </Box>
            <Box style={{ opacity: hasApplyWithSeek ? '1' : '0.5' }}>
              <Inline space="xsmall" alignY="center">
                {hasApplyWithSeek ? (
                  <IconTick tone="positive" size="xsmall" />
                ) : (
                  <IconMinus tone="secondary" size="xsmall" />
                )}
                <Text
                  size="small"
                  tone={hasApplyWithSeek ? 'neutral' : 'secondary'}
                >
                  Apply with SEEK
                </Text>
              </Inline>
            </Box>
          </Stack>
          <Stack space="small">
            {middleware && (
              <Badge tone="info" weight="regular">
                *Middleware required
              </Badge>
            )}
            {connectedApacIntegration && (
              <Badge tone="promote" weight="regular">
                APAC Integration
              </Badge>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Card>
  );
};
